// ─────────────────────────────────────────────────────────────────────────────
// Trip status -> tab classification. SINGLE SOURCE OF TRUTH, shared by the
// Bookings screen (Dashboard/Future.js) and My Schedule (Dashboard/MySchedule.js)
// so the two can never disagree about what counts as scheduled vs in-progress.
//
// Compared against a normalized status (lowercased, separators stripped), so
// "ArrivedDestination", "arrived_destination" and "Arrived Destination" all match
// the same entry.
//
// Canonical lifecycle (autodisp-master lib/itineraryHelpers.js):
//   OnHold -> Scheduled -> RouteAssigned -> Assigned/VehicleAssigned/
//   DriverAssigned -> Dispatched -> Enroute -> ArrivedOrigin -> Loaded ->
//   ArrivedDestination -> Completed
//
// Exact-set membership, not substring matching. A previous substring list had
// three defects worth remembering:
//   - 'Loaded' matched NOTHING, so a passenger-onboard trip read as SCHEDULED
//   - 'Dispatched' matched nothing either, so an en-route driver read as SCHEDULED
//   - 'Inactive' matched the key 'active' and read as ACTIVE
// ─────────────────────────────────────────────────────────────────────────────

/** In progress — the driver is actively working this trip. */
export const ACTIVE_STATUSES = new Set([
  'dispatched',
  'enroute',
  'inroute',
  'arrivedorigin',
  'arrived',
  'loaded',
  'onboard',
  'passengeronboard',
  'pickedup',
  'arriveddestination',
  'started',
  'inprogress',
]);

/** Finished/closed — belongs in Completed, never in SCHEDULED. */
export const TERMINAL_STATUSES = new Set([
  'completed',
  'complete',
  'cancelled',
  'canceled',
  'noshow',
  'no-show',
  'rejected',
  'void',
  'voided',
]);

export function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase().replace(/[\s_-]/g, '');
}

/**
 * @returns {'active'|'completed'|'scheduled'}
 */
export function classifyTripStatus(status) {
  const s = normalizeStatus(status);
  if (!s) return 'scheduled';
  if (ACTIVE_STATUSES.has(s)) return 'active';
  if (TERMINAL_STATUSES.has(s)) return 'completed';
  return 'scheduled';
}

export default classifyTripStatus;
